import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, Button, CircularProgress, Alert, TablePagination, Link, Stack,
  InputAdornment, FormControlLabel, Switch, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Grid, Divider, Tooltip, Checkbox,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import HubIcon from '@mui/icons-material/Hub';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import GroupsIcon from '@mui/icons-material/Groups';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  getMembers, loadNetwork, resetNetwork, getCertifications, issueCertification,
  getCredentialTypes, getSigningCredentials, getAppConfig, checkTrustTxt,
} from '../../services/api';
import { colorForRegistry, REGISTRY_COLORS } from '../../theme/registryColors';

const CERT_TYPE_ID = 'ethical-ai-certification';
const EAI_TYPE_ID = 'ethical-ai-certification';
const SUPERNODE_DOMAIN = 'auditedmedia.com';
const TRUST_GRAPH_VIEW = 'https://noosphere.news/trust-graph';

// The verified transparency indicators bound to each publisher's trust-graph
// node — the record the console can sign into a credential as-is.
const INDICATORS = [
  { key: 'has_ombudsperson', short: 'Ombudsperson' },
  { key: 'has_editorial_guidelines', short: 'Editorial guidelines' },
  { key: 'has_corrections_process', short: 'Corrections process' },
  { key: 'verifies_statistics', short: 'Verifies statistics' },
  { key: 'staff_can_organize', short: 'Staff can organize' },
];

// Compact row of green checks for the transparency indicators the graph asserts.
function IndicatorBadges({ metadata }) {
  const meta = metadata || {};
  const on = INDICATORS.filter((ind) => meta[ind.key] === true || meta[ind.key] === 'true');
  if (!on.length) return <Typography variant="caption" color="text.disabled">—</Typography>;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {on.map((ind) => (
        <Tooltip key={ind.key} title={ind.short} arrow>
          <Chip size="small" variant="outlined" icon={<CheckCircleIcon sx={{ color: '#76B900 !important' }} />}
            label={ind.short} sx={{ borderColor: 'rgba(118,185,0,0.4)', '& .MuiChip-label': { fontSize: 11 } }} />
        </Tooltip>
      ))}
    </Stack>
  );
}

// Cross-registry association chips (AAM · JTI · Journallist · IPTC · …) from the
// Trust Graph — colored consistently with the loader via the shared palette.
function AssociationChips({ associations }) {
  const list = associations || [];
  if (!list.length) return <Typography variant="caption" color="text.disabled">—</Typography>;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {list.map((a) => {
        const c = colorForRegistry(a.short);
        return (
          <Tooltip key={a.domain} title={a.name} arrow>
            <Chip size="small" label={a.short}
              sx={{ height: 20, bgcolor: `${c}22`, color: c, border: `1px solid ${c}66`,
                fontWeight: 700, '& .MuiChip-label': { fontSize: 11, px: 0.75 } }} />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

// A credential type's claims -> the input fields for issuance. Flat claims
// ({key,label,type,required}); legacy nested (claims->attributes) supported too.
function typeAttributes(type) {
  if (!type) return [];
  const claims = Array.isArray(type.claims_schema) ? type.claims_schema : [];
  return claims.flatMap((c) =>
    Array.isArray(c.attributes)
      ? c.attributes.map((a) => ({ name: a.name, label: a.description || a.name, required: a.required }))
      : [{ name: c.key || c.name, label: c.label || c.key || c.name, required: c.required }]
  );
}

// ---- Issue dialog: craft the VC from the template, choose signer + validity ----
function IssueDialog({ open, onClose, member, credentialTypes, signingCredentials, onIssued }) {
  const [typeId, setTypeId] = useState(CERT_TYPE_ID);
  const [signingCredentialId, setSigningCredentialId] = useState('');
  const [validityDays, setValidityDays] = useState(365);
  const [claims, setClaims] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const selectedType = useMemo(
    () => credentialTypes.find((t) => t.id === typeId) || credentialTypes.find((t) => t.id === CERT_TYPE_ID),
    [credentialTypes, typeId]
  );
  const attrs = useMemo(() => typeAttributes(selectedType), [selectedType]);

  // Default to the first available credential type (types start empty; the
  // operator creates them in the flow).
  useEffect(() => {
    if (credentialTypes.length && !credentialTypes.find((t) => t.id === typeId)) {
      setTypeId(credentialTypes[0].id);
    }
  }, [credentialTypes, typeId]);

  // Pre-fill claims from the member's trust-graph record — so the credential
  // signs the exact verified values the graph already holds (country, JTI
  // transparency indicators), falling back to the name for the publisher field.
  useEffect(() => {
    if (!member) return;
    const meta = member.metadata || {};
    const seed = {};
    attrs.forEach((a) => {
      if (meta[a.name] !== undefined && meta[a.name] !== null) seed[a.name] = String(meta[a.name]);
      else if (/publisher|name/i.test(a.name)) seed[a.name] = member.name || member.domain;
    });
    setClaims(seed);
  }, [member, attrs]);

  // Default the signer to the type's configured default signing key.
  useEffect(() => {
    setSigningCredentialId(selectedType?.default_signing_credential_id || '');
  }, [selectedType]);

  if (!member) return null;

  const handleIssue = async () => {
    try {
      setBusy(true); setErr(null);
      await issueCertification(member.domain, {
        credentialTypeId: typeId,
        signingCredentialId: signingCredentialId || undefined,
        validityDays: Number(validityDays) || undefined,
        claims,
      });
      onIssued();
      onClose();
    } catch (e) {
      console.error(e);
      setErr(e.response?.data?.error || 'Failed to issue. Make sure you are signed in.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, background: 'linear-gradient(135deg, rgba(118,185,0,0.18), transparent)', borderBottom: '2px solid #76B900' }}>
        <VerifiedIcon sx={{ color: '#76B900' }} /> Issue credential — <span style={{ color: '#76B900' }}>{member.domain}</span>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Subject: <code>did:web:{member.domain}</code>
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField select fullWidth label="Template" value={typeId}
              onChange={(e) => setTypeId(e.target.value)}>
              {credentialTypes.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField select fullWidth label="Signing credential" value={signingCredentialId}
              onChange={(e) => setSigningCredentialId(e.target.value)}
              helperText={signingCredentials.length ? 'Choose which key signs this VC' : 'No signers listed — default signer will be used'}>
              <MenuItem value=""><em>Default signer</em></MenuItem>
              {signingCredentials.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name || c.id}{c.is_default ? ' (default)' : ''}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField fullWidth type="number" label="Valid for (days)" value={validityDays}
              onChange={(e) => setValidityDays(e.target.value)} inputProps={{ min: 1 }} />
          </Grid>
          {attrs.length > 0 && (
            <Grid item xs={12}>
              <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Claims</Typography></Divider>
              {member.metadata && Object.keys(member.metadata).length > 2 && (
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, color: '#76B900' }}>
                  <VerifiedIcon fontSize="inherit" /> Prefilled from this publisher's Trust Graph record
                </Typography>
              )}
            </Grid>
          )}
          {attrs.map((a) => (
            <Grid item xs={12} sm={6} key={a.name}>
              <TextField fullWidth label={(a.label || a.name) + (a.required ? ' *' : '')}
                value={claims[a.name] || ''} helperText={a.name}
                onChange={(e) => setClaims((prev) => ({ ...prev, [a.name]: e.target.value }))} />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={handleIssue} disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : <VerifiedIcon />}
          sx={{ bgcolor: '#76B900', '&:hover': { bgcolor: '#5c8f00' }, fontWeight: 700 }}>
          Issue credential
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---- Members roster ----
const Members = () => {
  const [members, setMembers] = useState([]);
  const [org, setOrg] = useState(SUPERNODE_DOMAIN);
  const [certified, setCertified] = useState(new Set());
  const [credentialTypes, setCredentialTypes] = useState([]);
  const [signingCredentials, setSigningCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [certifiedOnly, setCertifiedOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dialogMember, setDialogMember] = useState(null);
  const [source, setSource] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [showTrustGraph, setShowTrustGraph] = useState(false);
  const [eaiCertified, setEaiCertified] = useState(new Set());
  const [eaiBusy, setEaiBusy] = useState(new Set());
  const [trustTxt, setTrustTxt] = useState({}); // domain -> bool (null = checking)

  const loadCertifications = useCallback(async () => {
    const certs = await getCertifications();
    // A member is "issued" if they hold any credential (any type).
    setCertified(new Set((certs || []).map((c) => c.member_domain)));
    // Track Ethical AI Certification holders specifically (drives the checkbox).
    setEaiCertified(new Set((certs || [])
      .filter((c) => c.credential_type_id === EAI_TYPE_ID)
      .map((c) => c.member_domain)));
  }, []);

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const roster = await getMembers();
      setMembers(roster.members || []);
      setSource(roster.source || 'trust.txt');
      await loadCertifications();
      // Types + signers are best-effort (signers need the vc-service API key).
      try { setCredentialTypes(await getCredentialTypes()); } catch (e) { /* noop */ }
      try { setSigningCredentials(await getSigningCredentials()); } catch (e) { setSigningCredentials([]); }
    } catch (e) {
      console.error(e);
      setError('Failed to load the member roster from the trust graph.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [loadCertifications]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getAppConfig().then((c) => { if (c?.orgDomain) setOrg(c.orgDomain); setShowTrustGraph(!!c?.showTrustGraphMetadata); }).catch(() => {}); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadNetwork();
    } catch (e) {
      setError('Could not load the publisher list from the Noosphere Trust Graph.');
    }
    await load(true);
    setRefreshing(false);
  };

  const handleReset = async () => {
    if (!window.confirm('Reset the demo? This clears all loaded member publishers and issued credentials.')) return;
    setRefreshing(true);
    try {
      await resetNetwork();
    } catch (e) {
      setError('Reset failed.');
    }
    await load(true);
    setRefreshing(false);
  };

  // Per-domain "enable Ethical AI Certification": issues the signed 8-pillar VC.
  // Enable-ONLY on purpose — a credential, once issued, can't be un-issued by
  // unticking a box. True revocation is a cryptographic operation (a published
  // status list the verifier checks), a separate feature. Use Reset to clear the
  // demo.
  const enableEai = async (m) => {
    if (eaiCertified.has(m.domain)) return;
    setEaiBusy((prev) => new Set(prev).add(m.domain));
    setError(null);
    try {
      await issueCertification(m.domain, {
        credentialTypeId: EAI_TYPE_ID,
        validityDays: 365,
        claims: {
          publisherName: m.name || m.domain,
          transparency: true, governance: true, biasFairness: true, privacy: true,
          disclosure: true, humanOversight: true, accountability: true, riskManagement: true,
          certifiedAt: new Date().toISOString().slice(0, 10),
        },
      });
      await loadCertifications();
    } catch (e) {
      setError('Failed to issue Ethical AI Certification.');
    } finally {
      setEaiBusy((prev) => { const n = new Set(prev); n.delete(m.domain); return n; });
    }
  };

  const rows = useMemo(() => {
    let r = members.map((m) => ({ ...m, certified: certified.has(m.domain) }));
    if (certifiedOnly) r = r.filter((m) => m.certified);
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((m) => m.domain.toLowerCase().includes(q) || (m.name || '').toLowerCase().includes(q));
    }
    r.sort((a, b) => (b.certified ? 1 : 0) - (a.certified ? 1 : 0) || a.domain.localeCompare(b.domain));
    return r;
  }, [members, certified, search, certifiedOnly]);

  const paged = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Live-check trust.txt for the visible page only (cached server-side).
  useEffect(() => {
    const need = paged.map((m) => m.domain).filter((d) => trustTxt[d] === undefined);
    if (!need.length) return;
    setTrustTxt((prev) => { const n = { ...prev }; need.forEach((d) => { n[d] = null; }); return n; });
    checkTrustTxt(need).then((res) => setTrustTxt((prev) => ({ ...prev, ...res }))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, rows]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (members.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <HubIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
        <Typography variant="h6" sx={{ mt: 2 }}>No member publishers loaded yet</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Load your network from the Noosphere Trust Graph to see your member publishers.
        </Typography>
        <Button variant="contained" onClick={handleRefresh} disabled={refreshing}
          startIcon={refreshing ? <CircularProgress size={16} /> : <GroupsIcon />}>
          Load member publishers
        </Button>
        {error && <Alert severity="info" sx={{ mt: 3, maxWidth: 480, mx: 'auto' }}>{error}</Alert>}
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HubIcon sx={{ color: '#76B900' }} /> Member Organizations
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <span><strong>{members.length}</strong> member organizations · <strong>{certified.size}</strong> with credentials issued</span>
            {source && <Chip size="small" label="From Noosphere Trust Graph" color="success" variant="outlined" />}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button variant="outlined" size="small" onClick={handleRefresh} disabled={refreshing}
            startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}>
            Refresh Trusted Publisher List
          </Button>
          <Button variant="outlined" size="small" color="error" onClick={handleReset} disabled={refreshing}
            startIcon={<RestartAltIcon />}>
            Reset
          </Button>
          <Link href={`${TRUST_GRAPH_VIEW}?domain=${encodeURIComponent(org)}&depth=1`} target="_blank" rel="noopener"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
            View trust graph <OpenInNewIcon fontSize="small" />
          </Link>
        </Stack>
      </Stack>

      {/* Registry color legend — matches the association chips + the loader. */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Trust registries:</Typography>
        {Object.entries(REGISTRY_COLORS).map(([short, c]) => (
          <Stack key={short} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c }} />
            <Typography variant="caption" sx={{ color: c, fontWeight: 700 }}>{short}</Typography>
          </Stack>
        ))}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField size="small" placeholder="Search members…" value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 260 }} />
        <FormControlLabel control={<Switch checked={certifiedOnly} onChange={(e) => { setCertifiedOnly(e.target.checked); setPage(0); }} />}
          label="Certified only" />
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Member publisher</TableCell>
              {showTrustGraph && <TableCell>Trust Graph record</TableCell>}
              <TableCell>Associations</TableCell>
              <TableCell align="center">Trust.txt</TableCell>
              <Tooltip title="One-click Ethical AI Certification — issues a signed verifiable credential" arrow>
                <TableCell align="center">Ethical AI</TableCell>
              </Tooltip>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map((m) => (
              <TableRow key={m.domain} hover>
                <TableCell>
                  {m.name
                    ? <span><strong>{m.name}</strong> <Typography component="span" variant="caption" color="text.secondary">{m.domain}</Typography></span>
                    : m.domain}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                    {m.metadata?.country && <Typography variant="caption" color="text.secondary">{m.metadata.country}</Typography>}
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled' }}>did:web:{m.domain}</Typography>
                  </Box>
                </TableCell>
                {showTrustGraph && <TableCell><IndicatorBadges metadata={m.metadata} /></TableCell>}
                <TableCell><AssociationChips associations={m.associations} /></TableCell>
                <TableCell align="center">
                  {trustTxt[m.domain] === undefined || trustTxt[m.domain] === null
                    ? <CircularProgress size={12} sx={{ color: 'text.disabled' }} />
                    : trustTxt[m.domain]
                      ? <Chip size="small" icon={<CheckCircleIcon />} label="trust.txt"
                          sx={{ height: 20, bgcolor: 'rgba(118,185,0,0.12)', color: '#5c8f00', '& .MuiChip-icon': { color: '#76B900' }, '& .MuiChip-label': { fontSize: 11 } }} />
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                </TableCell>
                <TableCell align="center">
                  {eaiCertified.has(m.domain)
                    ? <Tooltip title="Ethical AI Certification issued as a signed verifiable credential. Revoking is a separate cryptographic operation." arrow>
                        <Chip size="small" icon={<VerifiedIcon />} label="Certified"
                          sx={{ bgcolor: 'rgba(118,185,0,0.12)', color: '#5c8f00', '& .MuiChip-icon': { color: '#76B900' } }} />
                      </Tooltip>
                    : <Tooltip title="Issue the 8-pillar Ethical AI Certification for this publisher" arrow>
                        <span>
                          <Checkbox size="small"
                            checked={false}
                            disabled={eaiBusy.has(m.domain)}
                            onChange={() => enableEai(m)}
                            icon={<VerifiedIcon sx={{ color: '#76B900', opacity: 0.5 }} />}
                            checkedIcon={<VerifiedIcon />}
                            sx={{ color: '#76B900', '&.Mui-checked': { color: '#76B900' } }} />
                        </span>
                      </Tooltip>}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" variant={m.certified ? 'text' : 'contained'} onClick={() => setDialogMember(m)}>
                    {m.certified ? 'Re-issue' : 'Issue credential'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {paged.length === 0 && (
              <TableRow><TableCell colSpan={showTrustGraph ? 6 : 5}><Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No members match.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination component="div" count={rows.length} page={page}
          onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]} />
      </TableContainer>

      <IssueDialog open={!!dialogMember} member={dialogMember} onClose={() => setDialogMember(null)}
        credentialTypes={credentialTypes} signingCredentials={signingCredentials}
        onIssued={loadCertifications} />
    </Box>
  );
};

export default Members;
